<template>
  <div class="col-8 mx-auto">
    <!-- only staff added to a department as faculty can apply, being HOD doesn't count -->
    <b-alert :show="!inDepartment" variant="danger">
      You are not assigned to any department to apply for leave. Please contact your HOD.
    </b-alert>
    <b-button variant="primary" size="sm" :disabled="!inDepartment" @click="$bvModal.show('facultyLeaveFormModal')">
      Apply for Leave
    </b-button>

    <br><br>

    <div v-if="loading" class="text-center py-5">
      <b-spinner label="Loading..." variant="primary" class="mr-2"></b-spinner>
      <span class="font-weight-bold">Loading...</span>
    </div>

    <div v-else>
      <table class="table table-striped table-bordered">
        <thead class="thead-dark text-left">
          <tr>
            <th>Sl. No.</th>
            <th>Leave Type</th>
            <th>Session</th>
            <th>From</th>
            <th>To</th>
            <th>Reason</th>
            <th>Reject Reason</th>
            <th>Attachment</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="leaveRequests.length === 0">
            <td colspan="9" class="text-center text-muted py-4">No leave requests found.</td>
          </tr>
          <tr v-for="(leave, index) in leaveRequests" :key="leave.id">
            <td>{{ index + 1 }}</td>
            <td>{{ leave.leave_type ? leave.leave_type.name : '' }}</td>
            <td>{{ formatSession(leave.session) }}</td>
            <td>{{ formatDate(leave.from_date) }}</td>
            <td>{{ leave.session === 'full_day' ? formatDate(leave.to_date) : '-' }}</td>
            <td>{{ leave.reason }}</td>
            <td>{{ leave.reject_reason || '-' }}</td>
            <td class="text-center">
              <button v-if="leave.files && leave.files.length > 0"
                class="btn btn-sm btn-outline-secondary"
                @click="previewAttachment(leave.files[0])">
                <i class="fas fa-paperclip" style="font-size: 12px;"></i>
              </button>
            </td>
            <td>
              <span v-if="leave.status === 'approved'" class="text-success font-weight-bold">Approved</span>
              <span v-else-if="leave.status === 'rejected'" class="text-danger font-weight-bold">Rejected</span>
              <span v-else-if="leave.status === 'cancelled'" class="text-secondary font-weight-bold">Cancelled</span>
              <span v-else class="text-warning font-weight-bold">Pending</span>
              <template v-if="leave.status !== 'cancelled'">
                <small v-if="leave.recommendation_status === 'awaiting'" class="d-block text-muted">Awaiting HOD recommendation</small>
                <small v-else-if="leave.recommendation_status === 'recommended' && leave.status !== 'pending'" class="d-block text-muted">Recommended by HOD</small>
                <small v-else-if="leave.recommendation_status === 'not_recommended'" class="d-block text-muted">Rejected by HOD</small>
              </template>
            </td>
            <td class="text-center">
              <button v-if="leave.status === 'pending'"
                class="btn btn-sm btn-outline-danger"
                :disabled="cancelling === leave.id"
                @click="cancelLeave(leave.id)">
                {{ cancelling === leave.id ? 'Cancelling...' : 'Cancel' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <b-modal id="facultyLeaveFormModal" title="Apply for Leave" hide-footer no-close-on-backdrop centered @hidden="resetForm">
      <b-form @submit.prevent="submitLeave">

        <b-form-group>
          <template #label>Session <span class="text-danger">*</span></template>
          <b-form-radio-group v-model="form.session" :options="sessionOptions" buttons button-variant="outline-primary" size="sm"></b-form-radio-group>
        </b-form-group>

        <b-form-group>
          <template #label>{{ form.session === 'full_day' ? 'From Date' : 'Date' }} <span class="text-danger">*</span></template>
          <b-form-datepicker v-model="form.from_date" required></b-form-datepicker>
        </b-form-group>

        <b-form-group v-if="form.session === 'full_day'">
          <template #label>To Date <span class="text-danger">*</span></template>
          <b-form-datepicker v-model="form.to_date" :min="form.from_date" required></b-form-datepicker>
        </b-form-group>

        <b-form-group>
          <template #label>Leave Type <span class="text-danger">*</span></template>
          <div v-if="loadingLeaveTypes" class="text-muted small py-1">
            <b-spinner small></b-spinner> Loading leave types...
          </div>
          <small v-else-if="!datesReady" class="text-muted">Select date(s) first</small>
          <template v-else>
            <b-form-select v-model="form.leave_type_id" :options="leaveTypeOptions" required></b-form-select>
            <small v-if="selectedBalanceUnlimited" class="mt-1 d-block text-success">
              Remaining {{ selectedLeaveTypeName }}: <strong>NA</strong>
            </small>
            <small v-else-if="selectedBalance !== null" class="mt-1 d-block" :class="selectedBalance == 0 ? 'text-danger' : 'text-success'">
              Remaining {{ selectedLeaveTypeName }}: <strong>{{ selectedBalance }}</strong> {{ selectedBalance === 1 ? 'Day' : 'Days' }}
            </small>
          </template>
        </b-form-group>

        <b-form-group>
          <template #label>Reason <span class="text-danger">*</span></template>
          <b-form-input v-model="form.reason" required :state="form.reason.trim().length === 0 ? null : form.reason.trim().length >= 10"></b-form-input>
          <b-form-invalid-feedback>Reason must be at least 10 characters.</b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="Attachment (PDF only, max 800 KB)">
          <div class="d-flex align-items-center" style="gap: 8px;">
            <input type="file" ref="fileInput" @change="handleFile" accept=".pdf" />
            <span v-if="form.attachment" @click="clearFile" class="btn m-0 p-0 text-danger"><i class="fas fa-trash fa-sm" style="font-size: 1.5rem !important;"></i></span>
          </div>
        </b-form-group>

        <div class="d-flex justify-content-end" style="gap: 8px;">
          <b-button variant="danger" size="sm" @click="$bvModal.hide('facultyLeaveFormModal')">Cancel</b-button>
          <b-button type="submit" variant="success" size="sm" :disabled="saving">
            {{ saving ? 'Saving...' : 'Apply' }}
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      loading: false,
      saving: false,
      loadingLeaveTypes: false,
      cancelling: null,
      leaveRequests: [],
      inDepartment: true,
      leaveTypeOptions: [{ value: null, text: 'Select Leave Type', disabled: true }],
      leaveBalances: [],
      sessionOptions: [
        { value: 'forenoon', text: 'Forenoon' },
        { value: 'afternoon', text: 'Afternoon' },
        { value: 'full_day', text: 'Full Day' },
      ],
      cloudStorageRoot: "",
      form: {
        leave_type_id: null,
        session: 'full_day',
        from_date: "",
        to_date: "",
        reason: "",
        attachment: null,
      },
    };
  },

  computed: {
    datesReady() {
      if (!this.form.from_date) return false;
      if (this.form.session === 'full_day' && !this.form.to_date) return false;
      return true;
    },

    selectedBalance() {
      if (!this.form.leave_type_id) return null;
      const entry = this.leaveBalances.find(b => b.leave_type_id === this.form.leave_type_id);
      return entry != null ? entry.remaining_leaves ?? null : null;
    },

    // NA leave types in the faculty's group have no limit, shown as "NA" instead of a count
    selectedBalanceUnlimited() {
      if (!this.form.leave_type_id) return false;
      const entry = this.leaveBalances.find(b => b.leave_type_id === this.form.leave_type_id);
      return !!(entry && entry.is_unlimited);
    },

    selectedLeaveTypeName() {
      const option = this.leaveTypeOptions.find(o => o.value === this.form.leave_type_id);
      return option ? option.text : 'Leaves';
    },
  },

  watch: {
    'form.from_date'(val) {
      this.form.leave_type_id = null;
      if (val && this.datesReady) this.fetchLeaveData();
      else this.clearLeaveTypeData();
    },
    'form.to_date'(val) {
      this.form.leave_type_id = null;
      if (val && this.datesReady) this.fetchLeaveData();
      else this.clearLeaveTypeData();
    },
    'form.session'() {
      this.form.to_date = "";
      this.form.leave_type_id = null;
      this.clearLeaveTypeData();
      if (this.form.from_date && this.datesReady) this.fetchLeaveData();
    },
  },

  async created() {
    this.cloudStorageRoot = this.$store.getters.getAPIKey.userAttachments;
    await this.fetchMyLeaves();
  },

  methods: {
    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("en-GB");
    },

    clearLeaveTypeData() {
      this.leaveTypeOptions = [{ value: null, text: 'Select Leave Type', disabled: true }];
      this.leaveBalances = [];
    },

    async fetchLeaveData() {
      this.loadingLeaveTypes = true;
      this.clearLeaveTypeData();
      try {
        const params = { from_date: this.form.from_date };
        if (this.form.session === 'full_day' && this.form.to_date) params.to_date = this.form.to_date;

        const [typesRes, balancesRes] = await Promise.all([
          axios.get(`${this.$store.getters.getAPIKey.mainAPI}/faculty-leave-types`, { params }),
          axios.get(`${this.$store.getters.getAPIKey.mainAPI}/faculty-leave/balances`, { params }),
        ]);

        const types = Array.isArray(typesRes.data) ? typesRes.data : typesRes.data.data ?? [];
        this.leaveTypeOptions = [
          { value: null, text: 'Select Leave Type', disabled: true },
          ...types.map(t => ({ value: t.id, text: t.name })),
        ];
        this.leaveBalances = Array.isArray(balancesRes.data) ? balancesRes.data : balancesRes.data.data ?? [];
      } catch {
        this.$toast.error("Failed to load leave types for selected dates", { position: "top", duration: 3000 });
      } finally {
        this.loadingLeaveTypes = false;
      }
    },

    handleFile(e) {
      const file = e.target.files[0] || null;
      if (file && file.size > 800 * 1024) {
        this.$toast.error("File must be under 800 KB", { position: "top", duration: 3000 });
        this.clearFile();
        return;
      }
      this.form.attachment = file;
    },

    clearFile() {
      this.form.attachment = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },

    async fetchMyLeaves() {
      this.loading = true;
      try {
        const url = `${this.$store.getters.getAPIKey.mainAPI}/faculty-leave/request`;
        const response = await axios.get(url);
        this.leaveRequests = response.data.data ?? response.data ?? [];
        this.inDepartment = response.data.in_department !== false;
      } catch {
        this.$toast.error("Failed to load leave requests", { position: "top", duration: 3000 });
      } finally {
        this.loading = false;
      }
    },

    formatSession(session) {
      const map = { forenoon: 'Forenoon', afternoon: 'Afternoon', full_day: 'Full Day' };
      return map[session] ?? session ?? '-';
    },

    async submitLeave() {
      if (this.form.reason.trim().length < 10) {
        this.$toast.error("Reason must be at least 10 characters.", { position: "top", duration: 3000 });
        return;
      }
      this.saving = true;
      try {
        const formData = new FormData();
        formData.append("leave_type", this.form.leave_type_id);
        formData.append("session", this.form.session);
        formData.append("from_date", this.form.from_date);
        formData.append("to_date", this.form.session === 'full_day' ? this.form.to_date : this.form.from_date);
        formData.append("reason", this.form.reason);
        if (this.form.attachment) formData.append("attachment", this.form.attachment);

        await axios.post(
          `${this.$store.getters.getAPIKey.mainAPI}/faculty-leave/request`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        this.$toast.success("Leave request submitted", { position: "top", duration: 3000 });
        this.$bvModal.hide("facultyLeaveFormModal");
        this.resetForm();
        await this.fetchMyLeaves();
      } catch {
        // this.$toast.error("Failed to submit leave request", { position: "top", duration: 3000 });
      } finally {
        this.saving = false;
      }
    },

    async cancelLeave(id) {
      this.cancelling = id;
      try {
        await axios.post(`${this.$store.getters.getAPIKey.mainAPI}/faculty-leave/request/${id}/cancel`);
        this.$toast.success("Leave request cancelled", { position: "top", duration: 3000 });
        await this.fetchMyLeaves();
      } catch {
        this.$toast.error("Failed to cancel leave request", { position: "top", duration: 3000 });
      } finally {
        this.cancelling = null;
      }
    },

    previewAttachment(file) {
      window.open(this.cloudStorageRoot + file.file_path + file.file_name, "_blank");
    },

    resetForm() {
      this.form = { leave_type_id: null, session: 'full_day', from_date: "", to_date: "", reason: "", attachment: null };
      this.clearLeaveTypeData();
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
  },
};
</script>
